/* eslint-disable */

class VariantDrawerOption {
  constructor(fieldset) {
    this.fieldset = fieldset;
    this.trigger = fieldset.querySelector('.js-variant-drawer-trigger');
    this.panel = fieldset.querySelector('.js-variant-drawer-panel');
    this.backdrop = fieldset.querySelector('.js-variant-drawer-backdrop');
    this.closeBtn = fieldset.querySelector('.js-variant-drawer-close');
    this.triggerValue = fieldset.querySelector('.js-drawer-trigger-value');
    this.triggerSwatch = fieldset.querySelector('.js-drawer-trigger-swatch');

    this._handleKeydown = this._onKeydown.bind(this);
    this._panelAnchor = null;
    this._backdropAnchor = null;

    this._bindEvents();
    this._updatePanelState();
  }

  _bindEvents() {
    this.trigger.addEventListener('click', () => this.open());
    this.backdrop.addEventListener('click', () => this.close());
    this.closeBtn.addEventListener('click', () => this.close());

    // Inputs live in the fieldset; labels in the teleported panel trigger them via for=.
    // Listen here so we receive the change event regardless of panel position.
    this.fieldset.querySelectorAll('.js-option').forEach((input) => {
      input.addEventListener('change', (e) => {
        // Guard: ignore change events caused by scroll-triggered label taps
        if (this._isScrolling) return;
        this._syncTriggerFromChecked();
        this._updatePanelState();
        if (e.isTrusted && this.isOpen()) {
          setTimeout(() => this.close(), 130);
        }
      });
    });

    // Scroll-vs-tap guard for mobile:
    // <label for="..."> fires a click on the associated input when tapped,
    // but on mobile a scroll gesture starting on a label can be mistaken for a tap.
    // Track touchmove to distinguish scrolls from intentional taps.
    this._isScrolling = false;
    const scrollArea = this.panel.querySelector('.variant-drawer__scroll');
    if (scrollArea) {
      scrollArea.addEventListener('touchstart', () => {
        this._isScrolling = false;
      }, { passive: true });

      scrollArea.addEventListener('touchmove', () => {
        this._isScrolling = true;
      }, { passive: true });

      scrollArea.addEventListener('touchend', () => {
        // Reset after a short delay so genuine taps (no touchmove) pass through
        setTimeout(() => { this._isScrolling = false; }, 80);
      }, { passive: true });

      // Also intercept click on labels to prevent the input from firing
      this.panel.querySelectorAll('.opt-label').forEach((label) => {
        label.addEventListener('click', (e) => {
          if (this._isScrolling) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, { capture: true });
      });
    }

    // Sync after programmatic variant changes (e.g. color-filter auto-select)
    const variantPicker = this.fieldset.closest('variant-picker');
    if (variantPicker) {
      variantPicker.addEventListener('on:variant:change', () => {
        this._syncTriggerFromChecked();
        this._updatePanelState();
      });
    }
  }

  open() {
    // Teleport panel and backdrop to <body> so they escape any product-section
    // stacking context and always render above the sticky header.
    this._panelAnchor = { parent: this.panel.parentNode, next: this.panel.nextSibling };
    this._backdropAnchor = { parent: this.backdrop.parentNode, next: this.backdrop.nextSibling };
    document.body.appendChild(this.panel);
    document.body.appendChild(this.backdrop);

    this.panel.removeAttribute('inert');
    this.panel.removeAttribute('aria-hidden');
    document.addEventListener('keydown', this._handleKeydown);

    // Double rAF so CSS transition fires after element becomes visible
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        this.panel.classList.add('is-open');
        this.backdrop.classList.add('is-open');
      })
    );

    // Compensate for scrollbar disappearing so the page doesn't jump
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.setProperty('--vd-scrollbar-width', scrollbarWidth + 'px');
    document.body.classList.add('variant-drawer-open');

    // Inert all body-level siblings except panel, backdrop, and the element
    // containing the fieldset (labels need to reach inputs via for=).
    document.querySelectorAll('body > *').forEach((el) => {
      if (el !== this.panel && el !== this.backdrop && !el.contains(this.fieldset)) {
        el.dataset.vdInert = 'true';
        el.setAttribute('inert', '');
      }
    });

    this._updatePanelState();
    setTimeout(() => this.closeBtn && this.closeBtn.focus(), 60);
  }

  close() {
    if (!this.isOpen()) return;

    this.panel.classList.remove('is-open');
    this.backdrop.classList.remove('is-open');
    document.removeEventListener('keydown', this._handleKeydown);

    // Restore interactivity of body-level siblings
    document.querySelectorAll('[data-vd-inert]').forEach((el) => {
      el.removeAttribute('inert');
      delete el.dataset.vdInert;
    });

    const dur = this._transitionDuration();
    setTimeout(() => {
      // Return panel and backdrop to their original positions inside the fieldset
      this._restoreElement(this.panel, this._panelAnchor);
      this._restoreElement(this.backdrop, this._backdropAnchor);
      this._panelAnchor = null;
      this._backdropAnchor = null;

      this.panel.setAttribute('inert', '');
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('variant-drawer-open');
      document.body.style.removeProperty('--vd-scrollbar-width');
    }, dur);

    this.trigger.focus();
  }

  _restoreElement(el, anchor) {
    if (!anchor) return;
    if (anchor.next && anchor.next.parentNode === anchor.parent) {
      anchor.parent.insertBefore(el, anchor.next);
    } else {
      anchor.parent.appendChild(el);
    }
  }

  isOpen() {
    return this.panel.classList.contains('is-open');
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    // Focus trap: keep Tab cycling inside the panel
    if (e.key !== 'Tab') return;

    const focusable = [...this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )];
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  _transitionDuration() {
    const s = parseFloat(getComputedStyle(this.panel).transitionDuration);
    return isNaN(s) ? 350 : s * 1000;
  }

  /**
   * Sync .is-selected / .is-unavailable on panel labels from the real inputs
   * in the fieldset. Replaces CSS :checked + label (broken after separation).
   */
  _updatePanelState() {
    this.fieldset.querySelectorAll('.js-option').forEach((input) => {
      const label = this.panel.querySelector(`label[for="${input.id}"]`);
      if (!label) return;
      label.classList.toggle('is-selected', input.checked);
      label.classList.toggle(
        'is-unavailable',
        input.disabled || input.classList.contains('is-unavailable')
      );
    });
  }

  _syncTriggerFromChecked() {
    const checked = this.fieldset.querySelector('.js-option:checked');
    if (!checked) return;

    // Update text value
    if (this.triggerValue) {
      this.triggerValue.textContent = checked.value;
    }

    // Update swatch — labels are in the panel (may be at body level)
    if (!this.triggerSwatch) return;

    const label = this.panel.querySelector(`label[for="${checked.id}"]`);
    if (!label) return;

    // Copy data-swatch attribute (CSS color system)
    const swatchAttr = label.getAttribute('data-swatch');
    if (swatchAttr !== null) {
      this.triggerSwatch.setAttribute('data-swatch', swatchAttr);
    } else {
      this.triggerSwatch.removeAttribute('data-swatch');
    }

    // Copy inline CSS custom properties (native swatches)
    ['--swatch-color', '--swatch-image'].forEach((prop) => {
      const val = label.style.getPropertyValue(prop);
      if (val) {
        this.triggerSwatch.style.setProperty(prop, val);
      } else {
        this.triggerSwatch.style.removeProperty(prop);
      }
    });

    // For variant image swatches: sync the img src
    const labelImg = label.querySelector('img');
    const swatchImg = this.triggerSwatch.querySelector('img');
    if (labelImg && swatchImg) {
      swatchImg.src = labelImg.src;
      if (labelImg.srcset) swatchImg.srcset = labelImg.srcset;
    }
  }
}

function initVariantDrawers(root) {
  (root || document).querySelectorAll('.option-selector[data-has-drawer]').forEach((fieldset) => {
    if (!fieldset._vdInit) {
      fieldset._vdInit = true;
      new VariantDrawerOption(fieldset);
    }
  });
}

function initMediaVariantBadges(root) {
  (root || document).querySelectorAll('.js-media-variant-badge').forEach((badge) => {
    if (badge._mvbInit) return;
    badge._mvbInit = true;

    // Find the nearest product scope so we open the drawer for THIS product,
    // not the first one on the page (relevant for featured-product blocks, etc.)
    const scope = badge.closest('.js-product') || document;
    const trigger = scope.querySelector('.js-variant-drawer-trigger');

    if (!trigger) return; // No drawer exists — keep badge hidden

    badge.hidden = false;
    badge.addEventListener('click', () => trigger.click());
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initVariantDrawers();
    initMediaVariantBadges();
  });
} else {
  initVariantDrawers();
  initMediaVariantBadges();
}

// Re-init when quick-add drawer injects fresh product HTML
document.addEventListener('on:quick-add:after-open', (e) => {
  if (e.target) {
    initVariantDrawers(e.target);
    initMediaVariantBadges(e.target);
  }
});
